import React, {useCallback, useState, useRef} from 'react';
import ReactDOM from 'react-dom';
import Button from '@material-ui/core/Button';
import TextField from '@material-ui/core/TextField';
import theme from '../../../../theme';
import ColorSelect from '../ColorSelect';
import {debounce} from 'lodash';
import trackAnalytics from '../../../../utils/gaia/trackAnalytics';
import {TrackPopupProps} from './index';
import PopupCloseButton from '../PopupCloseButton';
import TrackStats from '../../TrackStats';
import ExpandMoreIcon from '@material-ui/icons/ExpandMore';
import Menu from '@material-ui/core/Menu';
import MenuItem from '@material-ui/core/MenuItem';
import {getTrackRemoteId} from '../../../../utils/gaia/saveTrack';
import useGetSyncPromise from '../../../../utils/react/useGetSyncPromise';

const debouncedEditTrackAttributesEvent = debounce(edited => {
    trackAnalytics('Edit Route Attributes', {edited, category: 'map2'});
}, 1000, {leading: false, trailing: true});

export default function WritableTrackPopup({
    trackLocalId,
    tracks,
    trackDispatcher,
    onTrackMoveStart,
    onPopupChange,
    project,
    interaction,
    mapboxLayerId
}: TrackPopupProps) {

    const track = tracks[trackLocalId];
    const {notes, title, name, color} = track.properties;

    const getSyncPromise = useGetSyncPromise();
    const remoteId = getSyncPromise(getTrackRemoteId(track))[0];
    const pathname = remoteId && `/datasummary/track/${remoteId}/`;

    const [moreOpen, setMoreOpen] = useState<boolean>(false);
    const moreRef = useRef();

    return <div style={{width: 250, display: 'flex', flexDirection: 'column'}}>
        <PopupCloseButton onClick={useCallback(() => onPopupChange(undefined), [onPopupChange])} />
        <div style={{display: 'flex', flexDirection: 'row'}}>
            <TextField
                value={title === undefined ? name : title}
                label={`${track.properties.track_type === 'route' ? 'Route' : 'Track'} Title`}
                margin='dense'
                variant='outlined'
                onChange={(event: any) => {
                    debouncedEditTrackAttributesEvent('Title');
                    trackDispatcher({
                        type: 'update',
                        save: true,
                        track: {
                            ...track,
                            properties: {...track.properties, title: event.target.value}
                        }
                    });
                }}
                style={{flex: 1, marginRight: theme.spacing(1)}}
                inputProps={{maxLength: 500}}
            />
            <ColorSelect
                value={color}
                onChange={(event: any) => {
                    debouncedEditTrackAttributesEvent('Color');
                    trackDispatcher({
                        type: 'update',
                        save: true,
                        track: {
                            ...track,
                            properties: {...track.properties, color: event.target.value}
                        }
                    });
                }}
            />
        </div>

        <TextField
            label="Notes"
            margin='dense'
            multiline
            onChange={(event: any) => {
                debouncedEditTrackAttributesEvent('Notes');
                trackDispatcher({
                    type: 'update',
                    save: true,
                    track: {
                        ...track,
                        properties: {...track.properties, notes: event.target.value}
                    }
                });
            }}
            rows={2}
            rowsMax={10}
            value={notes}
            variant='outlined'
        />

        <div style={{
            display: 'flex',
            flexDirection: 'row',
            marginTop: theme.spacing(1),
            alignItems: 'flex-end',
        }}>
            <TrackStats
                style={{flex: 1}}
                interaction={interaction}
                project={project}
                track={track}
            />

            {(window.location.pathname !== pathname || track.properties.track_type === 'route') &&
                <div>
                    <Button
                        onClick={() => setMoreOpen(!moreOpen)}
                        variant="contained"
                        color='primary'
                        buttonRef={moreRef}
                        style={{fontSize: 12, color: '#fff'}}
                    >
                            More
                        <ExpandMoreIcon style={{width: 16, marginRight: -4, marginLeft: 2}} />
                    </Button>

                    <Menu
                        onClose={() => setMoreOpen(false)}
                        open={moreOpen}
                        anchorEl={moreRef.current}
                    >
                        {window.location.pathname !== pathname && <MenuItem
                            href={pathname}
                            style={{fontSize: 12}}
                            onClick={() => trackAnalytics('View Route Details', {
                                category: 'map2'
                            })}
                            component='a'
                            disabled={!pathname}
                        >
                                Open Details Page
                        </MenuItem>}

                        {track.properties.track_type === 'route' && <MenuItem
                            disabled={!track.isGeometryLoaded}
                            onClick={() => onTrackMoveStart(
                                track.properties.trackLocalId,
                                mapboxLayerId
                            )}
                            style={{fontSize: 12}}
                        >
                            Edit Route
                        </MenuItem>}

                        {window.location.pathname !== pathname && <MenuItem
                            onClick={() => {
                                trackAnalytics('Delete Route', {category: 'map2'});
                                trackDispatcher({
                                    type: 'remove',
                                    trackLocalId,
                                    save: true,
                                });
                            }}
                            style={{fontSize: 12}}
                        >
                                Delete
                        </MenuItem>}
                    </Menu>
                </div>
            }
        </div>
    </div>;
}
